import {
  BellOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { ApprovalRule } from "@p0security/shared/types/workflow/types";
import { hasApprovalOptions } from "@p0security/shared/types/workflow/util";
import { Divider, Flex, Popover, Space, Typography } from "antd";
import { Logo } from "components/Integrations/Logo";
import { sortBy } from "lodash-es";
import { ReactNode } from "react";
import { stopPropagation } from "utils/javascript";
import { join } from "utils/join";

import { ApprovalOptionsPreview } from "../components/previews/ApprovalOptionsPreview";
import { ApprovalPreview } from "../components/previews/ApprovalPreview";

export const approvalTypeLabels: Record<ApprovalRule["type"], string> = {
  auto: "Auto-approve (on-call)",
  ticket: "Ticket approval",
  p0: "P0 Security Reviewers",
  group: "Group approval",
  deny: "Deny all",
  persistent: "Always allowed",
  escalation: "Escalation service",
  "requestor-profile": "Manager approval",
};

export function getApprovalIcon(type: ApprovalRule["type"]): ReactNode {
  switch (type) {
    case "auto":
      return <ClockCircleOutlined />;
    case "escalation":
      return <BellOutlined />;
    case "p0":
      return <Logo logo="/p0-dark-logomark.png" title="P0 Logomark" />;
    case "persistent":
      return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
    case "requestor-profile":
      return <UserOutlined />;
    case "group":
      return <TeamOutlined />;
    case "deny":
      return <StopOutlined style={{ color: "#f5222d" }} />;
  }
}

function renderFullApprovalList(record: ApprovalRule[]): ReactNode {
  const rendered = sortBy(record, "type").map((item: ApprovalRule, index) => (
    <Flex align="flex-start" gap={12} key={`${item.type}-${index}`} vertical>
      <div style={{ whiteSpace: "nowrap" }}>
        <ApprovalPreview approval={item} />
      </div>
      {hasApprovalOptions(item) && item.options && (
        <ApprovalOptionsPreview approvalOptions={item.options} />
      )}
    </Flex>
  ));

  return join(rendered, (index) => (
    <Divider size="small" key={`divider-${index}`} />
  ));
}

export const ApprovalSummary = ({
  record,
}: {
  record: ApprovalRule[] | null | undefined;
}) => {
  if (!record || !Array.isArray(record) || record.length === 0) {
    return "No approvals";
  }

  if (record.length === 1) {
    return renderFullApprovalList(record);
  }

  return (
    <Popover content={renderFullApprovalList(record)} trigger="hover">
      <Space size={8} style={{ cursor: "pointer" }} onClick={stopPropagation}>
        {sortBy(record, "type").map((item: ApprovalRule, index: number) => (
          <span
            key={index}
            style={{
              display: "inline-flex",
              alignItems: "center",
              width: 20,
              height: 20,
              fontSize: 16,
            }}
          >
            {getApprovalIcon(item.type)}
          </span>
        ))}
        <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
          {record.length} actions configured
        </Typography.Text>
      </Space>
    </Popover>
  );
};
